import React from "react";
import { Slider, Typography, FormHelperText, Box } from "@mui/material";

interface EmailLimitSliderProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  helperText?: string;
  warningThreshold?: number;
  warningText?: string;
  min?: number;
  max?: number;
  step?: number;
}

const EmailLimitSlider: React.FC<EmailLimitSliderProps> = ({
  value,
  onChange,
  label = "Email Limit",
  helperText = "Maximum number of emails that can be sent per day. This helps protect sender reputation and prevents email accounts from being flagged as spam.",
  warningThreshold = 30,
  warningText = "⚠️ Values over 30 may increase spam risk.",
  min = 1,
  max = 50,
  step = 1,
}) => {
  const isWarning = value > warningThreshold;

  // Generate marks dynamically based on max value
  const marks = [
    { value: min, label: min.toString() },
    { value: Math.floor(max / 4), label: Math.floor(max / 4).toString() },
    { value: Math.floor(max / 2), label: Math.floor(max / 2).toString() },
    { value: Math.floor((max * 3) / 4), label: Math.floor((max * 3) / 4).toString() },
    { value: max, label: max.toString() },
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>
          {label}: {value} per day
        </strong>
      </Typography>
      <Slider
        value={value}
        onChange={(e, newValue) => onChange(newValue as number)}
        min={min}
        max={max}
        step={step}
        marks={marks}
        sx={{
          mb: 2,
          "& .MuiSlider-thumb": {
            backgroundColor: isWarning ? "#f57c00" : undefined,
          },
          "& .MuiSlider-track": {
            backgroundColor: isWarning ? "#f57c00" : undefined,
          },
          "& .MuiSlider-rail": {
            backgroundColor: isWarning ? "#ffccbc" : undefined,
          },
        }}
      />
      <FormHelperText>
        {helperText}
        {isWarning && ` ${warningText}`}
      </FormHelperText>
    </Box>
  );
};

export default EmailLimitSlider;
